import React, { createContext, useContext, useEffect, useState } from "react";

const ThemeCtx = createContext(null);

// Stored preference is one of "light" | "dark" | "system".
// The actual rendered class on <html> is always concrete: "light" or "dark",
// resolved from OS preference when preference === "system".
const STORAGE_KEY = "tj_theme";

function resolveSystem() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function apply(pref) {
  const concrete = pref === "system" ? resolveSystem() : pref;
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(concrete);
  return concrete;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    // Migrate legacy values — anything not one of the three known values falls back to "system".
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
    return "system";
  });
  const [resolved, setResolved] = useState(() => apply(theme));

  useEffect(() => {
    setResolved(apply(theme));
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // When following the OS, react to live OS changes without requiring a reload.
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolved(apply("system"));
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const setTheme = (t) => {
    if (t === "light" || t === "dark" || t === "system") setThemeState(t);
  };

  // Cycle: system → light → dark → system → …
  const cycle = () => setThemeState(t => (t === "system" ? "light" : t === "light" ? "dark" : "system"));

  return (
    <ThemeCtx.Provider value={{ theme, resolved, setTheme, cycle, toggle: cycle }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
